// ── Shared Alignment Block Card ──
// Read-only lock card used wherever the Material Alignment Gate fails.
// Replaces the active work surface (no continue, no override) and offers
// only "View material details" + "Report friction".
//
// Used by:
//   • InstallerDashboardPage (push-mode work surface)
//   • PhaseExecutionPanel (ContractorPortal subcontractor view)
//
// Pure presentation. Consumes a verdict from materialAlignmentGate.

import { Card, CardContent } from "@/components/ui/card";
import { ReportFrictionButton } from "@/components/ReportFrictionButton";
import { Lock } from "lucide-react";
import { MaterialDetailsSheet } from "@/components/contractor/MaterialDetailsSheet";
import {
  describeAlignmentBlock,
  type MaterialAlignmentVerdict,
} from "@/data/materialAlignmentGate";

export function AlignmentBlockCard({
  verdict,
  jobId,
  moduleName = "Execution Surface",
}: {
  verdict: MaterialAlignmentVerdict;
  jobId: string;
  moduleName?: string;
}) {
  const copy = describeAlignmentBlock(verdict);

  // Sub-state headline so the operator knows WHO to contact:
  //   • Missing PO  → Ordering owns it
  //   • Missing Kit → Warehouse owns it
  //   • Mismatch    → re-prep / re-push
  const subState =
    verdict.missing.includes("po")
      ? "Order hasn't been pushed yet."
      : verdict.missing.includes("kit")
        ? "Warehouse hasn't built the kit yet."
        : verdict.missing.includes("spec")
          ? "Spec is not bound to this job."
          : "Spec, order, and kit don't match — re-prep order required.";

  return (
    <Card className="border-2 border-destructive/40 bg-destructive/[0.04]">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-2.5">
          <Lock className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-destructive mb-0.5">
              Do not begin install
            </p>
            <h2 className="text-base font-bold leading-tight">{copy.title}</h2>
            <p className="text-[12px] text-foreground/80 leading-snug mt-1">
              {subState}
            </p>
          </div>
        </div>

        <ul className="text-[11px] text-muted-foreground space-y-1 list-disc pl-5">
          {verdict.reasons.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>

        <div className="flex flex-wrap gap-2 pt-1">
          <MaterialDetailsSheet jobId={jobId} />
          <ReportFrictionButton moduleName={moduleName} />
        </div>
        <p className="text-[10px] text-muted-foreground">
          Contact your PM before proceeding. No override is available from this surface.
        </p>
      </CardContent>
    </Card>
  );
}
